import React, { useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { FlaskConical, Send, Bot, User, AlertCircle } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

export default function BotTestPanel() {
  const [message, setMessage] = useState('');
  const [reply, setReply] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [meta, setMeta] = useState(null);

  const handleTest = async () => {
    if (!message.trim()) return;
    setError('');
    setReply('');
    setMeta(null);
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/api/bot/test`, { message: message.trim() }, { withCredentials: true });
      setReply(data.reply);
      setMeta({ bot_type: data.bot_type, model: data.model });
    } catch (err) {
      setError(err.response?.data?.detail || 'Test failed');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey && !loading) {
      e.preventDefault();
      handleTest();
    }
  };

  return (
    <div className="border border-[#27272A] rounded-lg bg-[#1A1A1A] p-5 transition-all duration-200 hover:border-[#7B2FFF]/30">
      {/* Header */}
      <div className="flex items-center gap-2 mb-4">
        <FlaskConical className="w-4 h-4 text-[#7B2FFF]" />
        <h3 className="text-sm font-semibold text-[#EDEDF0]" style={{ fontFamily: "'Unbounded', sans-serif" }}>
          Test Your Bot
        </h3>
      </div>

      {/* Input row */}
      <div className="flex gap-2 mb-4">
        <Input
          data-testid="bot-test-input"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Type a sample customer message..."
          className="bg-[#111111] border-[#27272A] text-[#EDEDF0] placeholder:text-[#71717A] focus:border-[#7B2FFF] text-sm rounded-md h-10 font-mono flex-1"
        />
        <Button
          data-testid="bot-test-send-button"
          onClick={handleTest}
          disabled={loading || !message.trim()}
          className="bg-[#7B2FFF] hover:bg-[#9353FF] text-white font-medium rounded-md h-10 px-5 text-sm transition-all duration-200 hover:shadow-[0_0_16px_rgba(123,47,255,0.25)] active:scale-[0.98] disabled:opacity-40 shrink-0"
        >
          {loading ? (
            <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
          ) : (
            <span className="flex items-center gap-1.5"><Send className="w-3.5 h-3.5" /> Send Test</span>
          )}
        </Button>
      </div>

      {/* Error */}
      {error && (
        <div className="flex items-center gap-2 p-3 mb-4 border border-red-500/20 rounded-md bg-red-500/5 text-red-400 text-xs" data-testid="bot-test-error">
          <AlertCircle className="w-3.5 h-3.5 shrink-0" />
          {error}
        </div>
      )}

      {/* Response area */}
      {(reply || loading) && (
        <div className="terminal-bg rounded-md border border-[#27272A] overflow-hidden" data-testid="bot-test-response-area">
          {/* User message */}
          <div className="flex gap-3 px-4 py-3 border-b border-[#27272A]/50">
            <User className="w-4 h-4 text-[#A1A1AA] mt-0.5 shrink-0" />
            <p className="text-sm text-[#A1A1AA] font-mono">{message}</p>
          </div>

          {/* Bot reply */}
          <div className="flex gap-3 px-4 py-3 relative z-10">
            <Bot className="w-4 h-4 text-[#7B2FFF] mt-0.5 shrink-0" />
            <div className="flex-1 min-w-0">
              {loading ? (
                <div className="flex items-center gap-2">
                  <div className="flex gap-1">
                    <div className="w-1.5 h-1.5 rounded-full bg-[#7B2FFF] animate-bounce" style={{ animationDelay: '0ms' }} />
                    <div className="w-1.5 h-1.5 rounded-full bg-[#7B2FFF] animate-bounce" style={{ animationDelay: '150ms' }} />
                    <div className="w-1.5 h-1.5 rounded-full bg-[#7B2FFF] animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                  <span className="text-xs text-[#71717A] font-mono">Thinking...</span>
                </div>
              ) : (
                <p className="text-sm text-[#EDEDF0] font-mono leading-relaxed whitespace-pre-wrap" data-testid="bot-test-reply">
                  {reply}
                </p>
              )}
              {meta && !loading && (
                <p className="text-xs text-[#71717A] mt-2 font-mono">
                  {meta.bot_type.replace('_', ' ')} &middot; {meta.model}
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Disclaimer */}
      <p className="text-xs text-[#71717A] mt-3 italic" data-testid="bot-test-disclaimer">
        This is a simulated response — no GHL contact is created
      </p>
    </div>
  );
}
